import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, inr, num } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

export { inr, num };

export const useApi = (path, deps = []) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async (p = path) => {
    if (!p) return;
    setBusy(true);
    try { const res = await api.get(p); setData(res.data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, deps.concat([path]));
  return { data, busy, reload: load, setData };
};

export const post = async (path, body, okMsg) => {
  try {
    const { data } = await api.post(path, body);
    if (okMsg) toast.success(okMsg);
    return data;
  } catch (e) { toast.error(errMsg(e)); return null; }
};

export const put = async (path, body, okMsg) => {
  try {
    const { data } = await api.put(path, body);
    if (okMsg) toast.success(okMsg);
    return data;
  } catch (e) { toast.error(errMsg(e)); return null; }
};

export const Panel = ({ title, aside, children, testid }) => (
  <div className="rounded-lg border border-border bg-white" data-testid={testid}>
    {title && (
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3">
        <h3 className="font-display text-base font-semibold">{title}</h3>
        {aside}
      </div>
    )}
    <div className="p-5">{children}</div>
  </div>
);

export const PageHead = ({ eyebrow, title, subtitle, actions }) => (
  <div className="flex flex-wrap items-end justify-between gap-4">
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{eyebrow}</p>
      <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tighter">{title}</h1>
      {subtitle && <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{actions}</div>
  </div>
);

export const Stat = ({ label, value, onClick, testid }) => (
  <button onClick={onClick} disabled={!onClick} data-testid={testid}
          className={`rounded-lg border border-border bg-white p-4 text-left transition-shadow duration-200 ${onClick ? "hover:shadow-sm" : ""}`}>
    <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
    <p className="num mt-2 font-display text-2xl font-bold tracking-tight">{value}</p>
  </button>
);

/** Compact form: fields = [{key,label,type,options,readOnly}] */
export const QuickForm = ({ fields, values, onChange, onSubmit, submitLabel = "Save", testid, busy }) => (
  <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
        data-testid={testid}>
    {fields.map((f) => (
      <div key={f.key} className="space-y-1">
        <Label className="text-xs">{f.label}</Label>
        {f.options ? (
          <select value={values[f.key] ?? ""} onChange={(e) => onChange(f.key, e.target.value)}
                  data-testid={`${testid}-${f.key}`}
                  className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
            <option value="">Select…</option>
            {f.options.map((o) => (
              <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
            ))}
          </select>
        ) : (
          <Input type={f.type || "text"} value={values[f.key] ?? ""} readOnly={f.readOnly}
                 onChange={(e) => onChange(f.key, e.target.value)} className="bg-white"
                 data-testid={`${testid}-${f.key}`} />
        )}
      </div>
    ))}
    <div className="flex items-end sm:col-span-2 lg:col-span-3">
      <Button type="submit" disabled={busy} data-testid={`${testid}-submit`}>{submitLabel}</Button>
    </div>
  </form>
);

export const DataTable = ({ columns, rows, empty = "No records yet.", testid, onRowClick }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid={testid}>
      <thead>
        <tr className="border-b border-border text-xs uppercase text-muted-foreground">
          {columns.map((c) => (
            <th key={c.key} className={`px-2 py-2 ${c.right ? "text-right" : "text-left"}`}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(rows || []).map((r, i) => (
          <tr key={`${r.id ?? r.payout_id ?? r.lead_id ?? ""}-${i}`} onClick={() => onRowClick?.(r)}
              className={`border-b border-border/60 ${onRowClick ? "cursor-pointer hover:bg-muted/40" : ""}`}
              data-testid={`${testid}-row-${i}`}>
            {columns.map((c) => (
              <td key={c.key} className={`max-w-[240px] truncate px-2 py-2 ${c.right ? "num text-right" : ""}`}>
                {c.render ? c.render(r) : (r[c.key] ?? "—")}
              </td>
            ))}
          </tr>
        ))}
        {(!rows || rows.length === 0) && (
          <tr><td colSpan={columns.length} className="px-2 py-8 text-center text-sm text-muted-foreground">
            {empty}</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export const StatusBadge = ({ value }) => {
  const v = String(value || "");
  const style = /Approved|Disbursed|Active|Verified|Paid|Sanctioned/i.test(v)
    ? "bg-ok text-ok-foreground border-green-200"
    : /Hold|Pending|Query|Submitted|Under|Calculated|Draft/i.test(v)
      ? "bg-warn text-warn-foreground border-warn-border"
      : /Reject|Cancel|Inactive|Reversed|Expired|Withdrawn/i.test(v)
        ? "bg-red-50 text-red-700 border-red-200"
        : "bg-secondary text-secondary-foreground border-blue-200";
  return <Badge variant="outline" className={style}>{v || "—"}</Badge>;
};

export const Tabs = ({ tabs, active, onChange, testid }) => (
  <div className="flex flex-wrap gap-2 border-b border-border pb-2" data-testid={testid}>
    {tabs.map((t) => (
      <button key={t} onClick={() => onChange(t)} data-testid={`${testid}-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                active === t ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
        {t}
      </button>
    ))}
  </div>
);
